import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Play, Square, Download } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/timesheets")({
  head: () => ({ meta: [{ title: "Timesheets" }, { name: "robots", content: "noindex" }] }),
  component: TSPage,
});

type T = {
  id: string; employee_name: string; project_id: string | null; project_name: string | null;
  clock_in: string; clock_out: string | null; hours: number | null; notes: string | null;
};

const hoursBetween = (a: string, b: string) => Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 3600000);
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

function TSPage() {
  const [list, setList] = useState<T[]>([]);
  const [emp, setEmp] = useState("");
  const [project, setProject] = useState("");
  const [projects, setProjects] = useState<{ id: string; title: string }[]>([]);
  const [filter, setFilter] = useState({ employee: "", from: "", to: "" });
  const [manual, setManual] = useState<{ open: boolean; ci: string; co: string; notes: string }>({ open: false, ci: localInput(new Date()), co: localInput(new Date()), notes: "" });

  const load = async () => {
    const { data } = await supabase.from("timesheets").select("*").order("clock_in", { ascending: false });
    setList((data as any) || []);
    const { data: p } = await supabase.from("projects").select("id,title").order("created_at", { ascending: false });
    setProjects((p as any) || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const saved = localStorage.getItem("ts_emp"); if (saved) setEmp(saved); }, []);
  useEffect(() => { if (emp) localStorage.setItem("ts_emp", emp); }, [emp]);

  const active = useMemo(() => list.find(t => t.employee_name === emp && !t.clock_out), [list, emp]);

  const clockIn = async () => {
    if (!emp) return toast.error("Enter your name first");
    if (active) return toast.error("Already clocked in");
    const proj = projects.find(p => p.id === project);
    const { error } = await supabase.from("timesheets").insert({
      employee_name: emp, project_id: proj?.id || null, project_name: proj?.title || null, clock_in: new Date().toISOString(),
    });
    if (error) return toast.error(error.message);
    toast.success("Clocked in"); load();
  };
  const clockOut = async () => {
    if (!active) return;
    const out = new Date().toISOString();
    const hours = hoursBetween(active.clock_in, out);
    const { error } = await supabase.from("timesheets").update({ clock_out: out, hours }).eq("id", active.id);
    if (error) return toast.error(error.message);
    toast.success(`Clocked out · ${hours.toFixed(2)}h`); load();
  };
  const addManual = async () => {
    if (!emp) return toast.error("Enter your name first");
    const ci = new Date(manual.ci).toISOString(); const co = new Date(manual.co).toISOString();
    const hours = hoursBetween(ci, co);
    const proj = projects.find(p => p.id === project);
    const { error } = await supabase.from("timesheets").insert({
      employee_name: emp, project_id: proj?.id || null, project_name: proj?.title || null,
      clock_in: ci, clock_out: co, hours, notes: manual.notes || null,
    });
    if (error) return toast.error(error.message);
    toast.success("Entry added"); setManual({ ...manual, open: false, notes: "" }); load();
  };
  const del = async (id: string) => { if (confirm("Delete entry?")) { await supabase.from("timesheets").delete().eq("id", id); load(); } };

  const filtered = list.filter(t => {
    if (filter.employee && !t.employee_name.toLowerCase().includes(filter.employee.toLowerCase())) return false;
    if (filter.from && t.clock_in < filter.from) return false;
    if (filter.to && t.clock_in > filter.to + "T23:59:59Z") return false;
    return true;
  });

  const totalHours = filtered.reduce((s, t) => s + (t.hours || 0), 0);
  const byEmp: Record<string, number> = {};
  for (const t of filtered) byEmp[t.employee_name] = (byEmp[t.employee_name] || 0) + (t.hours || 0);

  const exportCSV = () => {
    const rows = [["Employee", "Project", "Clock In", "Clock Out", "Hours", "Notes"]];
    for (const t of filtered) rows.push([t.employee_name, t.project_name || "", t.clock_in, t.clock_out || "", (t.hours || 0).toFixed(2), t.notes || ""]);
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `timesheets-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Timesheets</h1>

      <div className="mt-6 rounded-lg border bg-card p-4">
        <h2 className="font-semibold mb-3">Clock In / Out</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><label className="text-xs text-muted-foreground">Employee</label><Input value={emp} onChange={e => setEmp(e.target.value)} placeholder="Your name" /></div>
          <div><label className="text-xs text-muted-foreground">Project (optional)</label>
            <select className="w-full rounded border border-input bg-background p-2" value={project} onChange={e => setProject(e.target.value)}>
              <option value="">— None —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            {active ? (
              <Button onClick={clockOut} variant="destructive" className="flex-1"><Square className="h-4 w-4 mr-1" />Clock Out</Button>
            ) : (
              <Button onClick={clockIn} className="flex-1"><Play className="h-4 w-4 mr-1" />Clock In</Button>
            )}
            <Button variant="outline" onClick={() => setManual(m => ({ ...m, open: !m.open }))}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        {active && <p className="text-sm text-emerald-600 mt-3">On the clock since {new Date(active.clock_in).toLocaleString()}</p>}
        {manual.open && (
          <div className="mt-4 grid gap-2 sm:grid-cols-4 border-t pt-4">
            <div><label className="text-xs text-muted-foreground">Clock In</label><Input type="datetime-local" value={manual.ci} onChange={e => setManual({ ...manual, ci: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground">Clock Out</label><Input type="datetime-local" value={manual.co} onChange={e => setManual({ ...manual, co: e.target.value })} /></div>
            <div className="sm:col-span-1"><label className="text-xs text-muted-foreground">Notes</label><Input value={manual.notes} onChange={e => setManual({ ...manual, notes: e.target.value })} /></div>
            <div className="flex items-end"><Button onClick={addManual} className="w-full">Add Manual Entry</Button></div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-muted-foreground">Filter Employee</label><Input value={filter.employee} onChange={e => setFilter({ ...filter, employee: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={filter.from} onChange={e => setFilter({ ...filter, from: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={filter.to} onChange={e => setFilter({ ...filter, to: e.target.value })} /></div>
        <Button variant="outline" onClick={exportCSV}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
        <div className="ml-auto text-right">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="font-display text-2xl font-bold">{totalHours.toFixed(2)}h</div>
        </div>
      </div>
      {Object.keys(byEmp).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(byEmp).map(([e, h]) => <span key={e} className="text-xs rounded-full bg-accent px-3 py-1">{e}: {h.toFixed(2)}h</span>)}
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b">
            <tr><th className="p-2">Employee</th><th className="p-2">Project</th><th className="p-2">Clock In</th><th className="p-2">Clock Out</th><th className="p-2 text-right">Hours</th><th className="p-2">Notes</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className="border-b">
                <td className="p-2">{t.employee_name}</td>
                <td className="p-2">{t.project_name || "—"}</td>
                <td className="p-2">{new Date(t.clock_in).toLocaleString()}</td>
                <td className="p-2">{t.clock_out ? new Date(t.clock_out).toLocaleString() : <span className="text-emerald-600">Active</span>}</td>
                <td className="p-2 text-right">{t.hours ? t.hours.toFixed(2) : "—"}</td>
                <td className="p-2">{t.notes}</td>
                <td className="p-2"><button onClick={() => del(t.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
